import { apiClient } from './apiClient.js';
import type {
  ApiResponse,
  PaginatedResponse,
  CandidateWithRole,
  CandidateDetail,
  PipelineResponse,
  CreateCandidateRequest,
  UpdateCandidateRequest,
  TransitionStageRequest,
  InterviewRecord,
  CreateInterviewRequest,
  UpdateInterviewRequest,
  DriveAgreement,
  DriveFile,
  BackgroundCheckRecord,
  InitiateBackgroundCheckRequest,
  InitiateCheckrBackgroundCheckRequest,
  UpdateBackgroundCheckRequest,
  CheckrPackage,
} from '@onboarding-dashboard/shared';

export const candidateService = {
  async list(params?: {
    page?: number;
    pageSize?: number;
    stage?: string;
    openRoleId?: string;
  }): Promise<ApiResponse<PaginatedResponse<CandidateWithRole>>> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.pageSize) query.set('pageSize', String(params.pageSize));
    if (params?.stage) query.set('stage', params.stage);
    if (params?.openRoleId) query.set('openRoleId', params.openRoleId);
    const qs = query.toString();
    return apiClient.get<PaginatedResponse<CandidateWithRole>>(`/candidates${qs ? `?${qs}` : ''}`);
  },

  async pipeline(): Promise<ApiResponse<PipelineResponse>> {
    return apiClient.get<PipelineResponse>('/candidates/pipeline');
  },

  async getById(id: string): Promise<ApiResponse<CandidateDetail>> {
    return apiClient.get<CandidateDetail>(`/candidates/${id}`);
  },

  async create(data: CreateCandidateRequest): Promise<ApiResponse<CandidateWithRole>> {
    return apiClient.post<CandidateWithRole>('/candidates', data);
  },

  async update(id: string, data: UpdateCandidateRequest): Promise<ApiResponse<CandidateWithRole>> {
    return apiClient.patch<CandidateWithRole>(`/candidates/${id}`, data);
  },

  async transitionStage(
    id: string,
    data: TransitionStageRequest
  ): Promise<ApiResponse<CandidateWithRole>> {
    return apiClient.post<CandidateWithRole>(`/candidates/${id}/stage`, data);
  },

  async createInterview(data: CreateInterviewRequest): Promise<ApiResponse<InterviewRecord>> {
    return apiClient.post<InterviewRecord>('/interviews', data);
  },

  async getInterview(id: string): Promise<ApiResponse<InterviewRecord>> {
    return apiClient.get<InterviewRecord>(`/interviews/${id}`);
  },

  async updateInterview(
    id: string,
    data: UpdateInterviewRequest
  ): Promise<ApiResponse<InterviewRecord>> {
    return apiClient.patch<InterviewRecord>(`/interviews/${id}`, data);
  },

  async cancelInterview(id: string): Promise<ApiResponse<InterviewRecord>> {
    return apiClient.delete<InterviewRecord>(`/interviews/${id}`);
  },

  // ── Drive Agreements ──────────────────────────────

  async getDriveAgreements(candidateId: string): Promise<ApiResponse<DriveAgreement[]>> {
    return apiClient.get<DriveAgreement[]>(`/candidates/${candidateId}/drive-agreements`);
  },

  async toggleAgreementSigned(
    candidateId: string,
    driveFileId: string,
    signed: boolean
  ): Promise<ApiResponse<void>> {
    return apiClient.post<void>(`/candidates/${candidateId}/drive-agreements/toggle-signed`, {
      driveFileId,
      signed,
    });
  },

  async uploadAgreement(candidateId: string, file: File): Promise<ApiResponse<DriveFile>> {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.upload<DriveFile>(
      `/candidates/${candidateId}/drive-agreements/upload`,
      formData
    );
  },

  // ── Resume ───────────────────────────────────────

  async getResumeFiles(candidateId: string): Promise<ApiResponse<DriveFile[]>> {
    return apiClient.get<DriveFile[]>(`/candidates/${candidateId}/resume-files`);
  },

  getResumeDownloadUrl(candidateId: string, fileId: string): string {
    const base = import.meta.env.VITE_API_URL || '/api';
    return `${base}/candidates/${candidateId}/resume-files/${fileId}/download`;
  },

  async uploadResume(candidateId: string, file: File): Promise<ApiResponse<DriveFile>> {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.upload<DriveFile>(`/candidates/${candidateId}/resume-upload`, formData);
  },

  // ── Background Checks ─────────────────────────────

  async initiateBackgroundCheck(
    data: InitiateBackgroundCheckRequest
  ): Promise<ApiResponse<BackgroundCheckRecord>> {
    return apiClient.post<BackgroundCheckRecord>('/background-checks', data);
  },

  async updateBackgroundCheck(
    id: string,
    data: UpdateBackgroundCheckRequest
  ): Promise<ApiResponse<BackgroundCheckRecord>> {
    return apiClient.patch<BackgroundCheckRecord>(`/background-checks/${id}`, data);
  },

  async listCheckrPackages(): Promise<ApiResponse<CheckrPackage[]>> {
    return apiClient.get<CheckrPackage[]>('/background-checks/packages');
  },

  async initiateCheckrBackgroundCheck(
    data: InitiateCheckrBackgroundCheckRequest
  ): Promise<ApiResponse<BackgroundCheckRecord>> {
    return apiClient.post<BackgroundCheckRecord>('/background-checks/checkr', data);
  },

  // ── Onboarding Documents ────────────────────────────

  async getOnboardingDocs(candidateId: string): Promise<ApiResponse<DriveFile[]>> {
    return apiClient.get<DriveFile[]>(`/candidates/${candidateId}/onboarding-docs`);
  },
};
